import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Tag, Switch, Row, Col } from 'antd';

import styled from 'styled-components';

import { getContactMainFeatures, getMetricPreference, getPreferredMetric } from '@libs/NwFeatures';

import { getModel, modelFullName } from '@hooks/API/Contact/UseContactsList';
import { useFeaturesIndex } from '@hooks/API/UseFeatures';

import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ConversionFilter from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallHeader/ConversionFilter';
import { hasFeatureValue } from '@components/Features/FeatureItemFormUtils';
import MainFeatures from '@components/Features/MainFeatures';
import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import RatingSelector from '@components/Gui/NwForm/RatingSelector';
import NwIcon from '@components/Gui/NwIcon';
import { ModelCover } from '@components/ModelCover';

import NumericFilter from './NumericFilter';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    background: white;
    padding: 0.5rem 1rem 1.5rem 1rem;
    margin-top: 0.5rem;

    .model-to-compare-container {
        display: flex;
        align-items: center;
        gap: 2rem;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        padding-bottom: 1rem;
        margin-bottom: 1rem;

        .model-to-compare-info {
            h3 {
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
                color: ${(props) => props.theme.TextColorMuted};
                margin: 0;
                line-height: 1.4;
                font-weight: normal;
            }
            h1 {
                font-size: ${(props) => props.theme.FontSizeLg};
                font-weight: bold;
                margin: 0;
                line-height: 1.4;
            }

            div.features {
                margin-top: 0.5em;
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                font-size: ${(props) => props.theme.FontSizeSm};
                text-transform: uppercase;

                dl {
                    display: inline-block;
                    margin-right: 0.5em;
                    margin-bottom: 0;
                    white-space: nowrap;
                    line-height: 1em;

                    dt {
                        display: inline-block;
                        padding: 0;
                        font-weight: normal;
                        color: ${(props) => props.theme.TextColorMuted};
                    }
                    dd {
                        display: inline-block;
                        padding: 0 0.25em;
                        color: ${(props) => props.theme.TextColor};
                        font-weight: bold;
                    }

                    &:after {
                        content: '•';
                    }

                    &:last-of-type {
                        margin-right: 0;
                        &:after {
                            content: none;
                        }
                    }
                }
            }
        }

        .model-to-compare-close {
            margin-left: 2rem;
        }
    }
`;

const StyledSwitch = styled(Switch)`
    margin-top: 4px;
`;

const FeatureLabel = styled.div`
    width: 100px;
`;

const ChoiceTag = styled(Tag)`
    background-color: ${(props) => (props.checked ? props.theme.PrimaryColor : props.theme.GreyedColor)};
    border-color: ${(props) => (props.checked ? props.theme.PrimaryColor : props.theme.GreyedColor)};
    color: white;
    cursor: pointer;
`;

const QuickFilterContainer = styled.div`
    display: flex;
    align-items: flex-start;
    margin-top: ${(props) => (props.$conversion ? '2rem' : '.5rem')};
`;

/**
 * La scelta configurata che corrisponde al valore di una feature. Gli ID sono GUID e il
 * payload delle modelle può restituirli con un case diverso da quello della configurazione,
 * quindi il confronto ignora maiuscole e minuscole.
 */
const findChoiceById = (choices, choiceID) => {
    const normalizedChoiceID = String(choiceID).toLowerCase();
    return choices.find((choice) => String(choice.ID).toLowerCase() === normalizedChoiceID);
};

const ModelFeaturesQuickFilters = ({ modelList }) => {
    const { context, selection } = useContactsSelector();
    const { quickFilters } = selection;
    const { featuresQuickFilters } = quickFilters;

    const { selectorAction, selectorActionID } = context;

    const cs_dispatch = useContactsSelectorDispatch();

    const featuresIndex = useFeaturesIndex();

    const [modelToCompare, setModelToCompare] = useState(null);
    const [modelFeatures, setModelFeatures] = useState(null);
    const [modelListCount, setModelListCount] = useState(0);
    const navigate = useNavigate();

    const updateFeaturesQuickFilters = (updatedFeaturesQuickFilters) => {
        cs_dispatch({
            type: 'ChangeQuickFilters',
            quickFilters: {
                ...quickFilters,
                featuresQuickFilters: updatedFeaturesQuickFilters,
            },
        });
    };

    const closeSimilarModelsPanel = () => {
        cs_dispatch({
            type: 'ClearSimilarModelsSearch',
        });
        navigate('/models/all/all');
    };

    useEffect(() => {
        if (selectorAction === 'find-similar' && selectorActionID) {
            const datamodeltocompare = getModel(Number(selectorActionID));
            setModelToCompare(datamodeltocompare);
        }
    }, [selectorAction, selectorActionID]);

    useEffect(() => {
        if (modelList && modelList.length && modelList.length !== modelListCount) {
            let features = [];
            if (modelList) {
                for (const model of modelList) {
                    for (const subFeature of getContactMainFeatures(model)) {
                        if (
                            subFeature.T === 'Choice' ||
                            subFeature.T === 'Conversion' ||
                            subFeature.T === 'Rating' ||
                            subFeature.T === 'Numeric'
                        ) {
                            let feature = features.find((feat) => feat.ID === subFeature.ID);
                            if (!feature) {
                                feature = { ...subFeature, values: [] };
                                features.push(feature);
                            }
                            // il filtro deve poter raggiungere anche i valori pubblici, non solo quelli standard
                            const subFeatureValues = hasFeatureValue(subFeature.PV)
                                ? [subFeature.V, subFeature.PV]
                                : [subFeature.V];
                            for (const subFeatureValue of subFeatureValues) {
                                if (!feature.values.find((value) => value === subFeatureValue)) {
                                    feature.values.push(subFeatureValue);
                                }
                            }
                        }
                    }
                }
            }
            setModelFeatures(features);
            setModelListCount(modelList.length);
        }
    }, [modelList]);

    useEffect(() => {
        if (modelToCompare && modelFeatures) {
            let featuresQuickFilters = {};
            for (const feature of getContactMainFeatures(modelToCompare)) {
                if (feature.T === 'Choice') {
                    featuresQuickFilters = {
                        ...featuresQuickFilters,
                        [feature.ID]: [feature.V],
                    };
                } else if (feature.T === 'Conversion') {
                    const featureFromModel = modelFeatures.find((item) => item.ID === feature.ID);
                    if (featureFromModel) {
                        const sortedValues = featureFromModel.values
                            .map((item) => parseInt(item))
                            .sort((v1, v2) => (v1 > v2 ? 1 : -1));
                        featuresQuickFilters = {
                            ...featuresQuickFilters,
                            [feature.ID]: [
                                parseInt(feature.V) > sortedValues[0] ? parseInt(feature.V) - 20 : parseInt(feature.V),
                                parseInt(feature.V) < sortedValues[sortedValues.length - 1]
                                    ? parseInt(feature.V) + 20
                                    : parseInt(feature.V),
                            ],
                        };
                    }
                } else if (feature.T === 'Numeric') {
                    const featureFromModel = modelFeatures.find((item) => item.ID === feature.ID);
                    if (featureFromModel) {
                        const sortedValues = featureFromModel.values
                            .map((item) => parseFloat(item))
                            .sort((v1, v2) => (v1 > v2 ? 1 : -1));
                        featuresQuickFilters = {
                            ...featuresQuickFilters,
                            [feature.ID]: [
                                parseFloat(feature.V) > sortedValues[0]
                                    ? parseFloat(feature.V) - 20
                                    : parseFloat(feature.V),
                                parseFloat(feature.V) < sortedValues[sortedValues.length - 1]
                                    ? parseFloat(feature.V) + 20
                                    : parseFloat(feature.V),
                            ],
                        };
                    }
                } else if (feature.T === 'Rating') {
                    featuresQuickFilters = {
                        ...featuresQuickFilters,
                        [feature.ID]: [feature.V],
                    };
                }
            }
            updateFeaturesQuickFilters(featuresQuickFilters);
        }
    }, [modelFeatures, modelToCompare]);

    const handleChangeFeaturesQuickFilters = (featureId, value) => {
        updateFeaturesQuickFilters({
            ...featuresQuickFilters,
            [featureId]: value,
        });
    };

    const renderFeature = (feature) => {
        switch (feature.T) {
            case 'Choice':
                const featureFromIndex = featuresIndex.featureById.get(feature.ID);
                const globalFeature =
                    featureFromIndex && featureFromIndex.TypeName === feature.T ? featureFromIndex : null;
                if (globalFeature) {
                    const choices = [];
                    for (const featureValue of feature.values) {
                        const choice = findChoiceById(globalFeature.Choices, featureValue);
                        // un valore che non corrisponde a nessuna scelta configurata non è filtrabile:
                        // si salta, altrimenti finisce nell'elenco come undefined.
                        // due valori che differiscono solo per il case portano alla stessa scelta:
                        // va aggiunta una volta sola
                        if (choice && !choices.includes(choice)) {
                            choices.push(choice);
                        }
                    }
                    choices.sort((choice1, choice2) => (choice1.Value > choice2.Value ? 1 : -1));
                    const value = featuresQuickFilters[feature.ID];

                    return (
                        <QuickFilterContainer>
                            <StyledSwitch
                                checked={value ? true : false}
                                size="small"
                                onChange={(checked) => {
                                    if (checked) {
                                        handleChangeFeaturesQuickFilters(
                                            feature.ID,
                                            choices.map((choice) => choice.ID)
                                        );
                                    } else {
                                        handleChangeFeaturesQuickFilters(feature.ID, null);
                                    }
                                }}
                            />
                            &nbsp;&nbsp;
                            <FeatureLabel>{feature.N}</FeatureLabel>
                            <div>
                                {choices.map((choice) => {
                                    const choiceValue = value ? value.find((item) => item === choice.ID) : null;
                                    return (
                                        <ChoiceTag
                                            checked={!value ? true : choiceValue}
                                            key={choice.ID}
                                            onClick={() => {
                                                if (!value || choiceValue) {
                                                    const featureChoices = (
                                                        value ? value : choices.map((choice) => choice.ID)
                                                    ).filter((item) => item !== choice.ID);
                                                    handleChangeFeaturesQuickFilters(feature.ID, featureChoices);
                                                } else {
                                                    handleChangeFeaturesQuickFilters(
                                                        feature.ID,
                                                        value ? [...value, choice.ID] : [choice.ID]
                                                    );
                                                }
                                            }}
                                        >
                                            {choice.Value}
                                        </ChoiceTag>
                                    );
                                })}
                            </div>
                        </QuickFilterContainer>
                    );
                }
                return `${feature.T}-${feature.ID}`;
            case 'Conversion':
                // il payload .FT porta direttamente il ConversionID
                const globalConversion = featuresIndex.conversionById.get(feature.C);
                if (globalConversion) {
                    const conversions = [];
                    const metric = getMetricPreference(globalConversion.ID, featuresIndex);
                    const preferredMetric = getPreferredMetric(globalConversion.ID, featuresIndex);
                    for (const featureValue of feature.values) {
                        const conversion = globalConversion.ConversionValues.find(
                            (conv) =>
                                conv.Key === parseInt(featureValue) &&
                                (!preferredMetric || preferredMetric === conv.Metric)
                        );
                        if (conversion && conversion.Key) {
                            conversions.push(conversion);
                        }
                    }

                    //conversions.sort((conv1, conv2) => parseFloat(conv1.Value.replace(',','.')) > parseFloat(conv2.Value.replace(',','.')) ? 1 : -1);
                    conversions.sort((conv1, conv2) => (parseInt(conv1.Key) > parseFloat(conv2.Key) ? 1 : -1));

                    return (
                        <QuickFilterContainer $conversion>
                            <StyledSwitch
                                checked={featuresQuickFilters[feature.ID] ? true : false}
                                size="small"
                                onChange={(checked) => {
                                    if (checked) {
                                        handleChangeFeaturesQuickFilters(feature.ID, [
                                            conversions[0].Key,
                                            conversions[conversions.length - 1].Key,
                                        ]);
                                    } else {
                                        handleChangeFeaturesQuickFilters(feature.ID, null);
                                    }
                                }}
                            />
                            &nbsp;&nbsp;
                            <FeatureLabel>{feature.N}</FeatureLabel>
                            <ConversionFilter
                                disabled={!featuresQuickFilters[feature.ID]}
                                conversions={conversions}
                                globalConversion={globalConversion}
                                metric={metric}
                                value={featuresQuickFilters[feature.ID]}
                                onChange={(value) => {
                                    handleChangeFeaturesQuickFilters(feature.ID, value);
                                }}
                            />
                        </QuickFilterContainer>
                    );
                }
                return `${feature.T} - ${feature.ID} - ${feature.C} - ${feature.N}`;
            case 'Rating':
                return (
                    <QuickFilterContainer>
                        <StyledSwitch
                            checked={featuresQuickFilters[feature.ID] ? true : false}
                            size="small"
                            onChange={(checked) => {
                                if (checked) {
                                    handleChangeFeaturesQuickFilters(feature.ID, 1);
                                } else {
                                    handleChangeFeaturesQuickFilters(feature.ID, null);
                                }
                            }}
                        />
                        &nbsp;&nbsp;
                        <FeatureLabel>{feature.N}</FeatureLabel>
                        <RatingSelector
                            simpleMode
                            onChange={(value) => handleChangeFeaturesQuickFilters(feature.ID, value)}
                            value={featuresQuickFilters[feature.ID]}
                        />
                    </QuickFilterContainer>
                );
            case 'Numeric':
                let minValue = 0;
                let maxValue = 0;
                for (const featureValue of feature.values) {
                    if (minValue === 0 || parseFloat(featureValue) < minValue) {
                        minValue = parseFloat(featureValue);
                    }
                    if (parseFloat(featureValue) > maxValue) {
                        maxValue = parseFloat(featureValue);
                    }
                }
                //const existingValues = feature.values.map(value => parseFloat(value)).sort((value1, value2) => value1 > value2 ? 1 : -1);
                return (
                    <QuickFilterContainer $conversion>
                        <StyledSwitch
                            checked={featuresQuickFilters[feature.ID] ? true : false}
                            size="small"
                            onChange={(checked) => {
                                if (checked) {
                                    handleChangeFeaturesQuickFilters(feature.ID, [minValue, maxValue]);
                                } else {
                                    handleChangeFeaturesQuickFilters(feature.ID, null);
                                }
                            }}
                        />
                        &nbsp;&nbsp;
                        <FeatureLabel>{feature.N}</FeatureLabel>
                        <NumericFilter
                            disabled={!featuresQuickFilters[feature.ID]}
                            minValue={minValue}
                            maxValue={maxValue}
                            value={featuresQuickFilters[feature.ID]}
                            onChange={(value) => {
                                handleChangeFeaturesQuickFilters(feature.ID, value);
                            }}
                        />
                    </QuickFilterContainer>
                );
            default:
                break;
        }
        return null;
    };

    return (
        <Wrapper>
            {modelToCompare && (
                <div className="model-to-compare-container">
                    <ModelCover model={modelToCompare} maxWidth={50} />
                    <div className="model-to-compare-info">
                        <h3>Find Models Similar To</h3>
                        <h1>{modelFullName(modelToCompare)}</h1>
                        <div className="features">
                            <MainFeatures contact={modelToCompare} />
                        </div>
                    </div>
                    <div className="model-to-compare-close">
                        <NwIcon
                            icon={byPrefixAndName.fass['times-circle']}
                            pointer
                            onClick={closeSimilarModelsPanel}
                            size="2x"
                        />
                    </div>
                </div>
            )}
            {modelFeatures ? (
                <Row gutter={16}>
                    {modelFeatures.map((feature) => {
                        return (
                            <Col lg={12} md={24} sm={24} xs={24} key={feature.ID}>
                                {renderFeature(feature)}
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <Loading />
            )}
        </Wrapper>
    );
};

export default ModelFeaturesQuickFilters;
